'use client'

import type React from 'react'

import { useRef, useState, useContext, createContext } from 'react'
import { SupportLink } from '@/components/SupportLink'
import type { JSX } from 'react/jsx-runtime'

export type NotificationContext = {
  show: boolean
  isError: boolean
  title?: string
  body?: string | JSX.Element
  variant?: NotificationVariant
  showNotification: (args: ShowNotificationOptions) => void
  hideNotification: () => void
}

export type NotificationState = {
  show: boolean
  isError: boolean
  title?: string
  body?: string | JSX.Element
  variant?: NotificationVariant
}

const missingNotificationProvider = 'Missing NotificationProvider'

export const NotificationContext = createContext<NotificationContext>({
  get show(): never {
    throw new Error(missingNotificationProvider)
  },
  get isError(): never {
    throw new Error(missingNotificationProvider)
  },
  get title(): never {
    throw new Error(missingNotificationProvider)
  },
  get body(): never {
    throw new Error(missingNotificationProvider)
  },
  showNotification: (): never => {
    throw new Error(missingNotificationProvider)
  },
  hideNotification: (): never => {
    throw new Error(missingNotificationProvider)
  },
})

const overrideMessages = {
  'Response too large': {
    title: 'Response size too large',
    body: (
      <>
        Please try selecting a smaller time period, lower granularity, or adding
        more filters. If you require the full dataset to be returned, please{' '}
        <SupportLink />.
      </>
    ),
  },
}

export const useNotification = () =>
  useContext<NotificationContext>(NotificationContext)

export type NotificationVariant = 'warning' | 'error' | 'default'
export type ShowNotificationOptions = {
  title: string
  isError: boolean
  body?: string | JSX.Element
  timeout?: number
  variant?: NotificationVariant
}

export const NotificationProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [notification, setNotification] = useState<NotificationState>({
    show: false,
    isError: false,
  })
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const showNotification = ({
    title: pTitle,
    body: pBody,
    isError,
    timeout = 3000,
    variant,
  }: ShowNotificationOptions): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }

    const message =
      typeof pBody === 'string'
        ? overrideMessages[pBody as keyof typeof overrideMessages]
        : undefined

    const title = message ? message?.title : pTitle
    const body = message ? message?.body : pBody

    setNotification({ show: true, title, body, isError, variant })
    // If it's not an error, hide the notification after 3 seconds
    if (!isError) {
      timeoutRef.current = setTimeout(() => {
        setNotification({ show: false, isError: false })
      }, timeout)
    }
  }

  const hideNotification = () => {
    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }

    // Set show to false immediately and reset all state
    setNotification({
      show: false,
      isError: false,
      title: undefined,
      body: undefined,
      variant: undefined,
    })
  }

  const value = { ...notification, showNotification, hideNotification }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}
